/**
 * THE SERVER'S REFUSALS, IN WORDS — one map, keyed by CODE.
 *
 * ── WHY THE SERVER DOES NOT SEND THE SENTENCE ─────────────────────────────
 *
 * The API answers `{ error: CODE, message: <short English> }`. `message` is for
 * whoever is reading a log; `error` is the contract. A server that sent the
 * user-facing sentence would be a server that has to know which of two people
 * is reading — the operator, in their own panel language, or nobody at all
 * because it is a background job — and it cannot know that. So the screen
 * translates, by code, and this is where.
 *
 * There is one deliberate exception, already documented on the API side: the
 * DESCRIPTION of an invoice line is DATA, written in the tenant's language,
 * because it is a line the customer reads. It is not a refusal and never comes
 * through here.
 *
 * ── WHAT BELONGS IN THIS FILE ─────────────────────────────────────────────
 *
 * Codes any screen can meet: authentication, subscription, validation,
 * contention, the generic conflicts. A code that only one feature can ever
 * receive stays with that feature — it passes its own sentence as the `fallback`
 * argument below. Copying eighty codes here to be exhaustive would produce a map
 * where nobody can tell which entries are still real.
 *
 * ── AND WHY THE SECOND HALF OF THE MAP IS GROUPED BY EMITTER ──────────────
 *
 * The rule above was written when the API still refused in Portuguese prose: a
 * feature could pass a sentence because the server had already said one. It no
 * longer does — the refusals were converted to codes, and a code with no entry
 * here reaches the reader as the caller's generic "could not save", which is the
 * loss the conversion was meant to prevent. So these codes are here.
 *
 * The mitigation for "nobody can tell which entries are still real" is the
 * grouping: every block below names the ROUTE that emits it, and an entry whose
 * emitter is gone is found by reading one comment rather than the whole map.
 *
 * ── THESE SENTENCES NAME NO DATA, ON PURPOSE ──────────────────────────────
 *
 * Several of these refusals carry data beside the code — `ALREADY_ON_FUNNEL`
 * sends `contact` and `pipeline`, `UNKNOWN_STAGE` sends `stages`. The sentences
 * here do not interpolate it, because `describeServerError` passes no parameters
 * and because the same code arrives WITHOUT the data on other routes
 * (`INVALID_DISPLAY`, `ROWS_WOULD_BREAK`), where a placeholder would render as a
 * hole. A screen that wants to name the record reads the payload itself and
 * passes its own sentence; a screen that does not still gets a true one.
 */
export const errorMessages = {
	en: {
		error: {
			/**
			 * O QUE UM AVISO DIZ QUANDO A FALHA NÃO TROUXE PALAVRA NENHUMA.
			 *
			 * Minúscula e sem o formato de CÓDIGO das outras chaves deste bloco de
			 * propósito: as outras são o nome de uma recusa que o servidor mandou, esta
			 * é o que `use-toast.ts` diz quando não recebeu recusa nenhuma — um
			 * `TypeError`, um fetch abortado, um `throw` sem mensagem. Estava escrita
			 * em português dentro do `use-toast.ts` e é, de longe, a frase que mais
			 * aparece no painel inteiro.
			 */
			generic: "Could not finish. Try again.",

			// Signing in, and staying signed in.
			INVALID_CREDENTIALS: "That e-mail and password do not match.",
			TOO_MANY_ATTEMPTS: "Too many attempts. Wait a moment and try again.",
			WRONG_PASSWORD: "Your current password does not match.",
			WEAK_PASSWORD: "The new password needs at least 12 characters.",
			INVALID_REFRESH: "Your session expired. Sign in again.",
			NO_SESSION: "Your session expired. Sign in again.",
			NO_ENTITY: "This list does not exist or is not open to you. Ask an admin if you need it.",
			LAST_OWNER: "This workspace needs at least one active owner. Promote someone else first.",

			// What the workspace is allowed to do at all.
			SUBSCRIPTION_EXPIRED: "Your trial has ended. Choose a plan to reactivate this workspace.",
			WORKSPACE_SUSPENDED: "This workspace is suspended.",
			NEEDS_DATA_PLAN: "Your plan does not include this.",
			ONBOARDING_BLOCKED: "Finish setting the workspace up first.",

			// What was sent.
			VALIDATION_ERROR: "Some fields need fixing.",
			REQUIRED_FIELD: "A required field is empty.",
			WINDOW_TAKEN: "This time is already taken. Pick another one.",
			INSUFFICIENT_BALANCE:
				"There is not enough balance for this — it would fall below the minimum.",
			CAPACITY_FULL: "This time is fully booked — there are not enough places left.",
			QUOTA_FULL: "The limit for this group is already reached.",
			INVALID_VALUE: "That value is not accepted here.",
			VALUE_TOO_LONG: "That value is too long.",
			VALUE_OUT_OF_RANGE: "That value is outside the allowed range.",
			INVALID_DATE: "An impossible date, or one in the wrong format.",
			INVALID_EMAIL: "That is not a valid e-mail address.",
			INVALID_URL: "That is not a valid link — it must start with http:// or https://.",
			INVALID_DOCUMENT: "That document number is not valid — check the digits.",
			FILE_NOT_FOUND: "That file does not exist in this workspace — attach it again.",
			INVALID_PHONE: "That phone number does not exist — check the country and the digits.",
			INVALID_FILE: "That is not a file from this workspace — attach it again.",
			INVALID_OPTION: "That option is not in this field's list.",
			SCHEDULE_IN_PAST: "That moment has already passed — pick a future date and time.",
			INVALID_INPUT: "One of the values is not in the expected format — dates are YYYY-MM-DD.",
			REFERENCE_NOT_FOUND: "That linked record does not exist or was deleted — pick another one.",
			INVALID_INTERVAL: "The end comes before the start — check the two times.",
			HAS_CHILDREN: "Other records still depend on this one — delete or move them first.",
			// Writes of a record (`field-value.ts`): what the field expects, said before the database.
			EXPECTED_INTEGER: "Must be a whole number.",
			/** A sentence plus the value the server refused (`params.received`). */
			received: "{sentence} Got “{value}”.",
			EXPECTED_NUMBER: "Must be a number.",
			EXPECTED_BOOLEAN: "Must be yes or no.",
			EXPECTED_TIME: "Must be a time in the format HH:MM.",
			VALUE_NOT_ALLOWED: "That value is not accepted by this field — check its options or format.",
			UNKNOWN_FIELD: "That field does not exist here.",
			INVARIANT_VIOLATED: "That would break a rule this record has to keep.",
			CONSTRAINT_VIOLATION: "The database refused that: it would break a rule.",
			ALREADY_EXISTS: "That already exists.",
			CODE_TAKEN: "That code is already in use.",
			NOTHING_TO_UPDATE: "Nothing was sent to change.",
			NEEDS_CONFIRMATION: "This needs confirming before it runs.",

			// The moment, rather than the request.
			CONTENTION: "Someone else changed this first. Reload and try again.",
			BUSY: "Still working on the last one. Try again in a moment.",
			TIMEOUT: "That took too long and was stopped.",
			UNAVAILABLE: "That service is not answering right now.",
			INTERNAL_ERROR: "Something went wrong on our side.",
			UPLOAD_REFUSED: "That file was refused.",
			SEND_FAILED: "It could not be sent.",

			// Putting something on a funnel — POST pipelines/:code/cards and the
			// contact shortcut beside it.
			PIPELINE_NOT_FOUND: "That funnel does not exist.",
			STAGE_NOT_FOUND: "That funnel has no such stage.",
			PIPELINE_HAS_NO_STAGES:
				"That funnel has no stages yet — create the first one and the card will have somewhere to go.",
			CONTACT_NOT_FOUND: "That person does not exist.",
			ALREADY_ON_FUNNEL: "This contact is already on that funnel.",
			CANNOT_PLACE: "That could not be placed on the funnel.",
			INVALID_VALUE_FIELD: "That is not a live numeric field of this screen.",

			// The channel → funnel shortcut (automation), which writes a rule for you.
			UNKNOWN_PIPELINE: "That funnel does not exist.",
			UNKNOWN_STAGE: "That funnel has no such stage.",
			NOT_CONTACT_CAPABLE:
				"That funnel does not take people: its screen has no field pointing at the contact (type ref_contact), and a channel can only put whoever wrote on a funnel. Give the screen such a field — or link the channel to the funnel that follows the person.",
			EMPTY_PIPELINE: "That funnel has no stages yet — create the first one and link again.",
			UNKNOWN_CHANNEL: "There is no channel of that type.",
			RULE_GONE: "That rule stopped existing.",
			HAND_WRITTEN:
				"A rule written by hand already sends this channel to a funnel. The shortcut leaves it alone — change or delete it in Automations.",

			// Records and fields (structure).
			RECORD_LOCKED: "This record was signed and can no longer be changed.",
			DERIVED_FIELD: "That field is calculated from linked records and cannot be typed into.",
			NOT_APPLICABLE: "That action does not apply to this record right now.",
			MISSING_PARAMETERS: "Some values this action needs were not filled in.",
			ROWS_WOULD_BREAK:
				"Some records already break this rule, so it could not be applied. Fix them first.",
			MISSING_LABEL: "A field needs a label.",
			INVALID_DISPLAY: "A field of that type cannot be shown that way.",
			UNKNOWN_ENTITY: "That screen does not exist.",
			ENTITY_EXISTS: "A screen with that code already exists. Pick another name.",
			FIELD_EXISTS: "This screen already has a field with that code.",
			RELATION_EXISTS: "These two screens are already linked.",
			VIEW_EXISTS: "This screen already has a view with that code.",
			SLUG_TAKEN: "That address is already taken by another view.",
			PUBLIC_FORM_MISSING_REQUIRED:
				"A public form has to ask every required field — add the missing ones to the form before publishing.",

			// The inbox, and connecting a channel.
			NOT_FOUND: "That is not here any more.",
			ALREADY_DECIDED: "Someone else decided this one first.",
			NOT_A_COMMENT: "That message did not come from a comment.",
			CHANNEL_GONE: "The channel behind this conversation no longer exists.",
			MODERATION_UNSUPPORTED: "This channel does not moderate comments.",
			PROVIDER_REFUSED: "The provider refused.",
			CHANNEL_LIMIT: "Your plan does not include another channel.",
			CONTACT_LIMIT:
				"This contact is over the plan's active-contact limit. Their messages are kept and open when the plan has room.",
			// `POST /v3/files/upload` com o armazenamento do plano cheio — toda porta de upload.
			STORAGE_LIMIT: "The plan's storage is full. Nothing was uploaded.",
			EXCHANGE_FAILED:
				"That could not be completed with Meta. Start the process again — the code works only once.",
			MISSING_SCOPES:
				"The authorisation did not include the WhatsApp permissions — check that the app uses Embedded Signup.",
			NOTHING_FOUND: "No WhatsApp Business account with a number was found in that authorisation.",
			NO_TOKEN: "This channel has no Meta token stored.",
			DO_NOT_CONTACT: "This contact asked not to be approached.",

			// Plans, packs and invoices — the billing the customer sees.
			NO_SUCH_PLAN: "There is no plan with that code.",
			PLAN_ARCHIVED: "That plan is no longer on sale.",
			TRIAL_ALREADY_USED: "The trial is a one-time entry — choose a plan to carry on.",
			CHECKOUT_UNAVAILABLE:
				"The payment gateway did not open a checkout. Nothing was changed — try again in a moment.",
			// Plano pago sem gateway (503, 28/09/2026): não há trilho manual para onde cair.
			GATEWAY_UNAVAILABLE:
				"Paid plans are charged by card, and card payments are not switched on in this installation. Nothing was changed.",
			NO_PLAN: "Choose a plan before carrying on.",
			PAID_NEEDS_CHECKOUT:
				"A paid plan only starts through the customer's checkout; from here only free plans can be set. Nothing was changed.",
			// Reactivating a suspended colleague takes a seat back. Saying which
			// ceiling was hit is what makes the next step obvious.
			USER_LIMIT: "This plan has no seat left for another active member.",
			// Um teste não tem cartão onde cobrar o pacote (e não há mais fatura para
			// onde ele iria). HÁ uma saída desta — dizê-la.
			TRIAL_RAIL:
				"Packs are for paid plans, and a trial is not one yet. Choose a plan and the packs open up.",
			// Troca de plano para o plano de TESTE (409, 27/09/2026).
			TRIAL_TARGET:
				"The trial is only for starting out: it cannot be the plan you change to. Choose one of the paid plans.",
			SUBSCRIPTION_CANCELLED: "This subscription is cancelled.",
			// O pacote é cobrado no cartão na hora, ou recusado — nunca vai para a fatura (28/09/2026).
			NO_CARD:
				"Packs are charged on the card on file, and this subscription has none. Add a card and try again.",
			/**
			 * O PACOTE COBRA NA HORA (0185): estes três chegam do 402, não do 409.
			 * O estado do workspace está perfeito — foi o banco que disse não.
			 */
			CHARGE_DECLINED:
				"The card was declined. Nothing was charged and no credits were added — check the card or use another one.",
			CHARGE_REQUIRES_ACTION:
				"Your bank asked to confirm this purchase. It is on hold until you authorise it; nothing was charged and no credits were added yet.",
			/** Não é um erro: é o desfecho bom do 3-D Secure, dito num aviso da casa. */
			CHARGE_AUTHENTICATED: "Authentication complete — the credits are in your balance.",
			NOT_WHOLE_BLOCKS: "This add-on is sold in whole blocks. Ask for a round number of them.",
			NO_SUCH_PACK: "There is no pack with that code.",
			PACK_ARCHIVED: "That pack is no longer on sale.",
			PACK_NOT_PRICED: "That pack is not on sale in your currency.",
			CANNOT_BUY_PACK: "The pack could not be bought.",
			CANNOT_CHOOSE_PLAN: "That plan could not be chosen.",
			NOT_FOR_SALE: "This is not sold self-service — talk to us.",
			BELOW_USAGE: "That is below what this workspace is already using.",
			EXTRAS_CHANGE_FAILED: "The add-ons could not be changed.",
			NO_SUBSCRIPTION: "This workspace has no subscription.",
			INVALID_REFERENCE_MONTH: "The reference month is YYYY-MM.",
			NO_INVOICE: "There is no invoice for that month.",
		},
		/**
		 * When there is no code at all — the request never reached a controller, or
		 * reached one that answered a bare status. Keyed by the transport's own
		 * taxonomy (`HttpErrorKind`), so every one of them has an answer.
		 */
		errorKind: {
			unauthorized: "Your session expired. Sign in again.",
			forbidden: "You do not have permission to do that.",
			notFound: "That is not here any more.",
			validation: "Some fields need fixing.",
			conflict: "That conflicts with something that already exists.",
			rateLimited: "Too many requests. Wait a moment.",
			paymentRequired: "That door is closed on this plan.",
			server: "Something went wrong on our side.",
			network: "Could not reach the server.",
			timeout: "The server took too long to answer.",
		},
	},
	"pt-BR": {
		error: {
			generic: "Não deu para concluir. Tente de novo.",
			INVALID_CREDENTIALS: "Esse e-mail e essa senha não conferem.",
			TOO_MANY_ATTEMPTS: "Tentativas demais. Espere um momento e tente de novo.",
			WRONG_PASSWORD: "A sua senha atual não confere.",
			WEAK_PASSWORD: "A senha nova precisa de pelo menos 12 caracteres.",
			INVALID_REFRESH: "Sua sessão expirou. Entre de novo.",
			NO_SESSION: "Sua sessão expirou. Entre de novo.",
			NO_ENTITY:
				"Esta lista não existe ou não está liberada para você. Peça a um admin se precisar dela.",
			LAST_OWNER: "Este workspace precisa de pelo menos um dono ativo. Promova outra pessoa antes.",

			SUBSCRIPTION_EXPIRED: "Seu teste acabou. Escolha um plano para reativar este workspace.",
			WORKSPACE_SUSPENDED: "Este workspace está suspenso.",
			NEEDS_DATA_PLAN: "O seu plano não inclui isto.",
			ONBOARDING_BLOCKED: "Termine de configurar o workspace primeiro.",

			VALIDATION_ERROR: "Alguns campos precisam de ajuste.",
			REQUIRED_FIELD: "Um campo obrigatório está vazio.",
			WINDOW_TAKEN: "Esse horário já está ocupado. Escolha outro.",
			INSUFFICIENT_BALANCE: "Não há saldo suficiente para isso — o saldo ficaria abaixo do mínimo.",
			CAPACITY_FULL: "Esse horário está lotado — não há vagas suficientes.",
			QUOTA_FULL: "O limite deste grupo já foi atingido.",
			INVALID_VALUE: "Esse valor não é aceito aqui.",
			VALUE_TOO_LONG: "Esse valor é comprido demais.",
			VALUE_OUT_OF_RANGE: "Esse valor está fora da faixa permitida.",
			INVALID_DATE: "Essa data não existe ou está no formato errado.",
			INVALID_EMAIL: "Esse e-mail não é válido.",
			INVALID_URL: "Esse link não é válido — ele precisa começar com http:// ou https://.",
			INVALID_DOCUMENT: "Esse documento não é válido — confira os dígitos.",
			FILE_NOT_FOUND: "Esse arquivo não existe neste workspace — anexe de novo.",
			INVALID_PHONE: "Esse telefone não existe — confira o país e os dígitos.",
			INVALID_FILE: "Isso não é um arquivo deste workspace — anexe de novo.",
			INVALID_OPTION: "Essa opção não está na lista deste campo.",
			SCHEDULE_IN_PAST: "Esse momento já passou — escolha uma data e hora no futuro.",
			INVALID_INPUT: "Um dos valores não está no formato esperado — confira a data.",
			REFERENCE_NOT_FOUND: "Esse registro vinculado não existe ou foi apagado — escolha outro.",
			INVALID_INTERVAL: "O fim vem antes do início — confira os dois horários.",
			HAS_CHILDREN: "Outros registros ainda dependem deste — apague ou mova-os antes.",
			EXPECTED_INTEGER: "Precisa ser um número inteiro.",
			received: "{sentence} Recebeu “{value}”.",
			EXPECTED_NUMBER: "Precisa ser um número.",
			EXPECTED_BOOLEAN: "Precisa ser sim ou não.",
			EXPECTED_TIME: "Precisa ser uma hora no formato HH:MM.",
			VALUE_NOT_ALLOWED:
				"Esse valor não é aceito por este campo — confira as opções ou o formato dele.",
			UNKNOWN_FIELD: "Esse campo não existe aqui.",
			INVARIANT_VIOLATED: "Isso quebraria uma regra que este registro tem de manter.",
			CONSTRAINT_VIOLATION: "O banco recusou: isso quebraria uma regra.",
			ALREADY_EXISTS: "Isso já existe.",
			CODE_TAKEN: "Esse código já está em uso.",
			NOTHING_TO_UPDATE: "Nada foi enviado para mudar.",
			NEEDS_CONFIRMATION: "Isto precisa ser confirmado antes de rodar.",

			CONTENTION: "Outra pessoa mudou isto antes. Recarregue e tente de novo.",
			BUSY: "Ainda estou terminando o anterior. Tente daqui a pouco.",
			TIMEOUT: "Isso demorou demais e foi interrompido.",
			UNAVAILABLE: "Esse serviço não está respondendo agora.",
			INTERNAL_ERROR: "Alguma coisa deu errado do nosso lado.",
			UPLOAD_REFUSED: "Esse arquivo foi recusado.",
			SEND_FAILED: "Não deu para enviar.",

			PIPELINE_NOT_FOUND: "Esse funil não existe.",
			STAGE_NOT_FOUND: "Esse funil não tem essa etapa.",
			PIPELINE_HAS_NO_STAGES:
				"Esse funil ainda não tem etapa nenhuma — crie a primeira e o cartão terá onde entrar.",
			CONTACT_NOT_FOUND: "Essa pessoa não existe.",
			ALREADY_ON_FUNNEL: "Este contato já está nesse funil.",
			CANNOT_PLACE: "Não deu para pôr isso no funil.",
			INVALID_VALUE_FIELD: "Esse não é um campo numérico vivo desta tela.",

			UNKNOWN_PIPELINE: "Esse funil não existe.",
			UNKNOWN_STAGE: "Esse funil não tem essa etapa.",
			NOT_CONTACT_CAPABLE:
				"Esse funil não recebe pessoas: a tela dele não tem um campo que aponte para o contato (tipo ref_contact), e um canal só sabe pôr no funil quem escreveu. Dê à tela um campo desse tipo — ou ligue o canal ao funil que acompanha a pessoa.",
			EMPTY_PIPELINE: "Esse funil ainda não tem etapa nenhuma — crie a primeira e ligue de novo.",
			UNKNOWN_CHANNEL: "Não existe canal desse tipo.",
			RULE_GONE: "Essa regra deixou de existir.",
			HAND_WRITTEN:
				"Uma regra escrita à mão já leva este canal a um funil. O atalho não mexe nela — ajuste ou apague em Automações.",

			RECORD_LOCKED: "Este registro foi assinado e não pode mais ser alterado.",
			DERIVED_FIELD:
				"Esse campo é calculado a partir dos registros ligados e não pode ser digitado.",
			NOT_APPLICABLE: "Essa ação não se aplica a este registro agora.",
			MISSING_PARAMETERS: "Faltam valores que essa ação precisa.",
			ROWS_WOULD_BREAK:
				"Alguns registros já não obedecem a essa regra, então ela não pôde ser aplicada. Corrija-os antes.",
			MISSING_LABEL: "O campo precisa de um rótulo.",
			INVALID_DISPLAY: "Um campo desse tipo não pode ser exibido assim.",
			UNKNOWN_ENTITY: "Essa tela não existe.",
			ENTITY_EXISTS: "Já existe uma tela com esse código. Escolha outro nome.",
			FIELD_EXISTS: "Esta tela já tem um campo com esse código.",
			RELATION_EXISTS: "Estas duas telas já estão ligadas.",
			VIEW_EXISTS: "Esta tela já tem uma visão com esse código.",
			SLUG_TAKEN: "Esse endereço já está em uso por outra visão.",
			PUBLIC_FORM_MISSING_REQUIRED:
				"Um formulário público precisa perguntar todos os campos obrigatórios — coloque os que faltam no formulário antes de publicar.",

			NOT_FOUND: "Isso não está mais aqui.",
			ALREADY_DECIDED: "Outra pessoa já decidiu esta.",
			NOT_A_COMMENT: "Essa mensagem não veio de um comentário.",
			CHANNEL_GONE: "O canal desta conversa não existe mais.",
			MODERATION_UNSUPPORTED: "Este canal não modera comentários.",
			PROVIDER_REFUSED: "O provedor recusou.",
			CHANNEL_LIMIT: "O seu plano não inclui mais um canal.",
			CONTACT_LIMIT:
				"Este contato está acima do limite de contatos ativos do plano. As mensagens ficam guardadas e abrem quando o plano tiver folga.",
			STORAGE_LIMIT: "O armazenamento do plano está cheio. Nada foi enviado.",
			EXCHANGE_FAILED:
				"Não deu para concluir com a Meta. Comece o processo de novo — o código só vale uma vez.",
			MISSING_SCOPES:
				"A autorização não incluiu as permissões do WhatsApp — verifique se o app usa a configuração de Embedded Signup.",
			NOTHING_FOUND:
				"Nenhuma conta do WhatsApp Business com número foi encontrada nessa autorização.",
			NO_TOKEN: "Este canal não tem um token da Meta guardado.",
			DO_NOT_CONTACT: "Este contato pediu para não ser procurado.",

			NO_SUCH_PLAN: "Não existe plano com esse código.",
			PLAN_ARCHIVED: "Esse plano não está mais à venda.",
			TRIAL_ALREADY_USED:
				"O período de teste é uma entrada única — escolha um plano para continuar.",
			CHECKOUT_UNAVAILABLE:
				"O gateway de pagamento não abriu o checkout. Nada foi alterado — tente de novo em instantes.",
			GATEWAY_UNAVAILABLE:
				"Planos pagos são cobrados no cartão, e o pagamento por cartão não está ligado nesta instalação. Nada foi alterado.",
			NO_PLAN: "Escolha um plano antes de continuar.",
			PAID_NEEDS_CHECKOUT:
				"Plano pago só começa pelo checkout do cliente; daqui só dá para pôr planos grátis. Nada foi alterado.",
			USER_LIMIT: "Este plano não tem assento livre para mais um membro ativo.",
			TRIAL_RAIL:
				"Pacotes são para planos pagos, e um teste ainda não é um. Escolha um plano e os pacotes liberam.",
			// Troca de plano para o plano de TESTE (409, 27/09/2026).
			TRIAL_TARGET:
				"O teste é só para começar: não pode ser o plano de destino de uma troca. Escolha um dos planos pagos.",
			SUBSCRIPTION_CANCELLED: "Esta assinatura está cancelada.",
			NO_CARD:
				"Pacotes são cobrados no cartão cadastrado, e esta assinatura não tem um. Cadastre um cartão e tente de novo.",
			CHARGE_DECLINED:
				"O cartão foi recusado. Nada foi cobrado e nenhum crédito entrou — confira o cartão ou use outro.",
			CHARGE_REQUIRES_ACTION:
				"Seu banco pediu para confirmar esta compra. Ela fica em espera até você autorizar; nada foi cobrado e nenhum crédito entrou ainda.",
			CHARGE_AUTHENTICATED: "Autenticação concluída — os créditos já estão no seu saldo.",
			NOT_WHOLE_BLOCKS:
				"Este adicional é vendido em blocos inteiros. Peça um número redondo deles.",
			NO_SUCH_PACK: "Não existe pacote com esse código.",
			PACK_ARCHIVED: "Esse pacote não está mais à venda.",
			PACK_NOT_PRICED: "Esse pacote não está à venda na sua moeda.",
			CANNOT_BUY_PACK: "Não foi possível comprar o pacote.",
			CANNOT_CHOOSE_PLAN: "Não foi possível escolher esse plano.",
			NOT_FOR_SALE: "Isto não está à venda por aqui — fale conosco.",
			BELOW_USAGE: "Isso é menos do que este workspace já está usando.",
			EXTRAS_CHANGE_FAILED: "Não foi possível mudar os adicionais.",
			NO_SUBSCRIPTION: "Este workspace não tem assinatura.",
			INVALID_REFERENCE_MONTH: "O mês de referência é AAAA-MM.",
			NO_INVOICE: "Não há fatura desse mês.",
		},
		errorKind: {
			unauthorized: "Sua sessão expirou. Entre de novo.",
			forbidden: "Você não tem permissão para fazer isso.",
			notFound: "Isso não está mais aqui.",
			validation: "Alguns campos precisam de ajuste.",
			conflict: "Isso conflita com alguma coisa que já existe.",
			rateLimited: "Requisições demais. Espere um momento.",
			paymentRequired: "Esta porta está fechada neste plano.",
			server: "Alguma coisa deu errado do nosso lado.",
			network: "Não consegui falar com o servidor.",
			timeout: "O servidor demorou demais para responder.",
		},
	},
	el: {
		error: {
			generic: "Δεν ολοκληρώθηκε. Δοκίμασε ξανά.",

			INVALID_CREDENTIALS: "Αυτό το email και ο κωδικός δεν ταιριάζουν.",
			TOO_MANY_ATTEMPTS: "Πάρα πολλές προσπάθειες. Περίμενε λίγο και δοκίμασε ξανά.",
			WRONG_PASSWORD: "Ο τρέχων κωδικός σου δεν ταιριάζει.",
			WEAK_PASSWORD: "Ο νέος κωδικός χρειάζεται τουλάχιστον 12 χαρακτήρες.",
			INVALID_REFRESH: "Η συνεδρία σου έληξε. Συνδέσου ξανά.",
			NO_SESSION: "Η συνεδρία σου έληξε. Συνδέσου ξανά.",
			NO_ENTITY:
				"Αυτή η λίστα δεν υπάρχει ή δεν είναι ανοιχτή για εσάς. Ζητήστε τη από έναν διαχειριστή αν τη χρειάζεστε.",
			LAST_OWNER:
				"Αυτός ο χώρος εργασίας χρειάζεται τουλάχιστον έναν ενεργό ιδιοκτήτη. Προήγαγε πρώτα κάποιον άλλον.",

			SUBSCRIPTION_EXPIRED:
				"Η δοκιμή σου τελείωσε. Διάλεξε πλάνο για να ενεργοποιήσεις ξανά αυτόν τον χώρο εργασίας.",
			WORKSPACE_SUSPENDED: "Αυτός ο χώρος εργασίας είναι σε αναστολή.",
			NEEDS_DATA_PLAN: "Το πλάνο σου δεν το περιλαμβάνει αυτό.",
			ONBOARDING_BLOCKED: "Ολοκλήρωσε πρώτα τη ρύθμιση του χώρου εργασίας.",

			VALIDATION_ERROR: "Κάποια πεδία χρειάζονται διόρθωση.",
			REQUIRED_FIELD: "Ένα υποχρεωτικό πεδίο είναι κενό.",
			WINDOW_TAKEN: "Αυτή η ώρα είναι ήδη κλεισμένη. Διάλεξε άλλη.",
			INSUFFICIENT_BALANCE: "Δεν υπάρχει αρκετό υπόλοιπο — θα έπεφτε κάτω από το ελάχιστο.",
			CAPACITY_FULL: "Αυτή η ώρα είναι πλήρης — δεν υπάρχουν αρκετές θέσεις.",
			QUOTA_FULL: "Το όριο αυτής της ομάδας έχει ήδη συμπληρωθεί.",
			INVALID_VALUE: "Αυτή η τιμή δεν γίνεται δεκτή εδώ.",
			VALUE_TOO_LONG: "Αυτή η τιμή είναι πολύ μεγάλη.",
			VALUE_OUT_OF_RANGE: "Αυτή η τιμή είναι εκτός του επιτρεπτού εύρους.",
			INVALID_DATE: "Αυτή η ημερομηνία δεν υπάρχει ή έχει λάθος μορφή.",
			INVALID_EMAIL: "Αυτό το e-mail δεν είναι έγκυρο.",
			INVALID_URL: "Αυτός ο σύνδεσμος δεν είναι έγκυρος — πρέπει να ξεκινά με http:// ή https://.",
			INVALID_DOCUMENT: "Αυτός ο αριθμός εγγράφου δεν είναι έγκυρος — ελέγξτε τα ψηφία.",
			FILE_NOT_FOUND: "Αυτό το αρχείο δεν υπάρχει σε αυτόν τον χώρο εργασίας — επισυνάψτε το ξανά.",
			INVALID_PHONE: "Αυτός ο αριθμός τηλεφώνου δεν υπάρχει — ελέγξτε τη χώρα και τα ψηφία.",
			INVALID_FILE: "Αυτό δεν είναι αρχείο αυτού του χώρου εργασίας — επισυνάψτε το ξανά.",
			INVALID_OPTION: "Αυτή η επιλογή δεν υπάρχει στη λίστα αυτού του πεδίου.",
			SCHEDULE_IN_PAST: "Αυτή η στιγμή έχει ήδη περάσει — επιλέξτε μελλοντική ημερομηνία και ώρα.",
			INVALID_INPUT: "Μία από τις τιμές δεν έχει την αναμενόμενη μορφή — ελέγξτε την ημερομηνία.",
			REFERENCE_NOT_FOUND: "Αυτή η συνδεδεμένη εγγραφή δεν υπάρχει ή διαγράφηκε — επιλέξτε άλλη.",
			INVALID_INTERVAL: "Το τέλος είναι πριν από την αρχή — ελέγξτε τις δύο ώρες.",
			HAS_CHILDREN:
				"Άλλες εγγραφές εξαρτώνται ακόμη από αυτήν — διαγράψτε ή μετακινήστε τις πρώτα.",
			EXPECTED_INTEGER: "Πρέπει να είναι ακέραιος αριθμός.",
			received: "{sentence} Δόθηκε «{value}».",
			EXPECTED_NUMBER: "Πρέπει να είναι αριθμός.",
			EXPECTED_BOOLEAN: "Πρέπει να είναι ναι ή όχι.",
			EXPECTED_TIME: "Πρέπει να είναι ώρα στη μορφή HH:MM.",
			VALUE_NOT_ALLOWED:
				"Αυτό το πεδίο δεν δέχεται αυτή την τιμή — ελέγξτε τις επιλογές ή τη μορφή του.",
			UNKNOWN_FIELD: "Αυτό το πεδίο δεν υπάρχει εδώ.",
			INVARIANT_VIOLATED: "Αυτό θα έσπαγε έναν κανόνα που πρέπει να τηρεί αυτή η εγγραφή.",
			CONSTRAINT_VIOLATION: "Η βάση δεδομένων το αρνήθηκε: θα έσπαγε έναν κανόνα.",
			ALREADY_EXISTS: "Αυτό υπάρχει ήδη.",
			CODE_TAKEN: "Αυτός ο κωδικός χρησιμοποιείται ήδη.",
			NOTHING_TO_UPDATE: "Δεν στάλθηκε τίποτα για αλλαγή.",
			NEEDS_CONFIRMATION: "Αυτό χρειάζεται επιβεβαίωση πριν τρέξει.",

			CONTENTION: "Κάποιος άλλος το άλλαξε πρώτος. Κάνε ανανέωση και δοκίμασε ξανά.",
			BUSY: "Το προηγούμενο δεν έχει τελειώσει ακόμη. Δοκίμασε ξανά σε λίγο.",
			TIMEOUT: "Αυτό άργησε πολύ και διακόπηκε.",
			UNAVAILABLE: "Αυτή η υπηρεσία δεν απαντά αυτή τη στιγμή.",
			INTERNAL_ERROR: "Κάτι πήγε στραβά από την πλευρά μας.",
			UPLOAD_REFUSED: "Αυτό το αρχείο απορρίφθηκε.",
			SEND_FAILED: "Δεν ήταν δυνατή η αποστολή.",

			PIPELINE_NOT_FOUND: "Αυτή η χοάνη δεν υπάρχει.",
			STAGE_NOT_FOUND: "Αυτή η χοάνη δεν έχει τέτοιο στάδιο.",
			PIPELINE_HAS_NO_STAGES:
				"Αυτή η χοάνη δεν έχει ακόμη στάδια — δημιούργησε το πρώτο και η κάρτα θα έχει πού να πάει.",
			CONTACT_NOT_FOUND: "Αυτό το άτομο δεν υπάρχει.",
			ALREADY_ON_FUNNEL: "Αυτή η επαφή βρίσκεται ήδη σε εκείνη τη χοάνη.",
			CANNOT_PLACE: "Δεν ήταν δυνατή η τοποθέτησή του στη χοάνη.",
			INVALID_VALUE_FIELD: "Αυτό δεν είναι ενεργό αριθμητικό πεδίο αυτής της οθόνης.",

			UNKNOWN_PIPELINE: "Αυτή η χοάνη δεν υπάρχει.",
			UNKNOWN_STAGE: "Αυτή η χοάνη δεν έχει τέτοιο στάδιο.",
			NOT_CONTACT_CAPABLE:
				"Αυτή η χοάνη δεν δέχεται άτομα: η οθόνη της δεν έχει πεδίο που να δείχνει στην επαφή (τύπος ref_contact), και ένα κανάλι μπορεί να βάλει στη χοάνη μόνο όποιον έγραψε. Δώσε στην οθόνη ένα τέτοιο πεδίο — ή σύνδεσε το κανάλι με τη χοάνη που παρακολουθεί το άτομο.",
			EMPTY_PIPELINE: "Αυτή η χοάνη δεν έχει ακόμη στάδια — δημιούργησε το πρώτο και σύνδεσε ξανά.",
			UNKNOWN_CHANNEL: "Δεν υπάρχει κανάλι αυτού του τύπου.",
			RULE_GONE: "Αυτός ο κανόνας έπαψε να υπάρχει.",
			HAND_WRITTEN:
				"Ένας κανόνας γραμμένος στο χέρι στέλνει ήδη αυτό το κανάλι σε μια χοάνη. Η συντόμευση δεν τον αγγίζει — άλλαξέ τον ή διάγραψέ τον στους Αυτοματισμούς.",

			RECORD_LOCKED: "Αυτή η εγγραφή υπογράφηκε και δεν μπορεί πια να αλλάξει.",
			DERIVED_FIELD:
				"Αυτό το πεδίο υπολογίζεται από τις συνδεδεμένες εγγραφές και δεν συμπληρώνεται με πληκτρολόγηση.",
			NOT_APPLICABLE: "Αυτή η ενέργεια δεν ισχύει για αυτή την εγγραφή αυτή τη στιγμή.",
			MISSING_PARAMETERS: "Κάποιες τιμές που χρειάζεται αυτή η ενέργεια δεν συμπληρώθηκαν.",
			ROWS_WOULD_BREAK:
				"Κάποιες εγγραφές παραβιάζουν ήδη αυτόν τον κανόνα, οπότε δεν μπόρεσε να εφαρμοστεί. Διόρθωσέ τες πρώτα.",
			MISSING_LABEL: "Ένα πεδίο χρειάζεται ετικέτα.",
			INVALID_DISPLAY: "Ένα πεδίο αυτού του τύπου δεν μπορεί να εμφανιστεί έτσι.",
			UNKNOWN_ENTITY: "Αυτή η οθόνη δεν υπάρχει.",
			ENTITY_EXISTS: "Υπάρχει ήδη οθόνη με αυτόν τον κωδικό. Διαλέξτε άλλο όνομα.",
			FIELD_EXISTS: "Αυτή η οθόνη έχει ήδη πεδίο με αυτόν τον κωδικό.",
			RELATION_EXISTS: "Αυτές οι δύο οθόνες είναι ήδη συνδεδεμένες.",
			VIEW_EXISTS: "Αυτή η οθόνη έχει ήδη προβολή με αυτόν τον κωδικό.",
			SLUG_TAKEN: "Αυτή η διεύθυνση χρησιμοποιείται ήδη από άλλη προβολή.",
			PUBLIC_FORM_MISSING_REQUIRED:
				"Μια δημόσια φόρμα πρέπει να ρωτά όλα τα υποχρεωτικά πεδία — προσθέστε όσα λείπουν πριν τη δημοσίευση.",

			NOT_FOUND: "Αυτό δεν είναι πια εδώ.",
			ALREADY_DECIDED: "Κάποιος άλλος το αποφάσισε πρώτος.",
			NOT_A_COMMENT: "Αυτό το μήνυμα δεν προήλθε από σχόλιο.",
			CHANNEL_GONE: "Το κανάλι πίσω από αυτή τη συνομιλία δεν υπάρχει πια.",
			MODERATION_UNSUPPORTED: "Αυτό το κανάλι δεν κάνει έλεγχο σχολίων.",
			PROVIDER_REFUSED: "Ο πάροχος αρνήθηκε.",
			CHANNEL_LIMIT: "Το πλάνο σου δεν περιλαμβάνει άλλο κανάλι.",
			CONTACT_LIMIT:
				"Αυτή η επαφή είναι πάνω από το όριο ενεργών επαφών του πλάνου. Τα μηνύματα φυλάσσονται και ανοίγουν όταν το πλάνο έχει περιθώριο.",
			STORAGE_LIMIT: "Ο αποθηκευτικός χώρος του πλάνου γέμισε. Δεν ανέβηκε τίποτα.",
			EXCHANGE_FAILED:
				"Δεν ήταν δυνατή η ολοκλήρωση με τη Meta. Ξεκίνα τη διαδικασία από την αρχή — ο κωδικός ισχύει μόνο μία φορά.",
			MISSING_SCOPES:
				"Η εξουσιοδότηση δεν περιλάμβανε τα δικαιώματα του WhatsApp — έλεγξε ότι η εφαρμογή χρησιμοποιεί Embedded Signup.",
			NOTHING_FOUND:
				"Δεν βρέθηκε λογαριασμός WhatsApp Business με αριθμό σε αυτή την εξουσιοδότηση.",
			NO_TOKEN: "Αυτό το κανάλι δεν έχει αποθηκευμένο token της Meta.",
			DO_NOT_CONTACT: "Αυτή η επαφή ζήτησε να μην την προσεγγίσουν.",

			NO_SUCH_PLAN: "Δεν υπάρχει πλάνο με αυτόν τον κωδικό.",
			PLAN_ARCHIVED: "Αυτό το πλάνο δεν πωλείται πια.",
			TRIAL_ALREADY_USED:
				"Η δοκιμαστική περίοδος είναι μία και μοναδική — διάλεξε πλάνο για να συνεχίσεις.",
			CHECKOUT_UNAVAILABLE:
				"Η πύλη πληρωμών δεν άνοιξε τη σελίδα πληρωμής. Τίποτα δεν άλλαξε — δοκιμάστε ξανά σε λίγο.",
			GATEWAY_UNAVAILABLE:
				"Τα πληρωμένα πλάνα χρεώνονται στην κάρτα, και οι πληρωμές με κάρτα δεν είναι ενεργές σε αυτή την εγκατάσταση. Τίποτα δεν άλλαξε.",
			NO_PLAN: "Διάλεξε πλάνο πριν συνεχίσεις.",
			PAID_NEEDS_CHECKOUT:
				"Ένα πληρωμένο πλάνο ξεκινά μόνο από το checkout του πελάτη· από εδώ μπαίνουν μόνο δωρεάν πλάνα. Τίποτα δεν άλλαξε.",
			USER_LIMIT: "Αυτό το πλάνο δεν έχει άλλη θέση για ένα ακόμη ενεργό μέλος.",
			TRIAL_RAIL:
				"Τα πακέτα είναι για πληρωμένα πλάνα, και η δοκιμή δεν είναι ακόμη. Διάλεξε πλάνο και τα πακέτα ξεκλειδώνουν.",
			// Troca de plano para o plano de TESTE (409, 27/09/2026).
			TRIAL_TARGET:
				"Η δοκιμή είναι μόνο για την αρχή: δεν μπορεί να είναι το πλάνο προορισμού μιας αλλαγής. Επιλέξτε ένα από τα πληρωμένα πλάνα.",
			SUBSCRIPTION_CANCELLED: "Αυτή η συνδρομή είναι ακυρωμένη.",
			NO_CARD:
				"Τα πακέτα χρεώνονται στην αποθηκευμένη κάρτα, και αυτή η συνδρομή δεν έχει. Πρόσθεσε κάρτα και δοκίμασε ξανά.",
			CHARGE_DECLINED:
				"Η κάρτα απορρίφθηκε. Δεν χρεώθηκε τίποτα και δεν προστέθηκαν μονάδες — ελέγξτε την κάρτα ή χρησιμοποιήστε άλλη.",
			CHARGE_REQUIRES_ACTION:
				"Η τράπεζά σας ζήτησε επιβεβαίωση για αυτή την αγορά. Μένει σε αναμονή μέχρι να την εγκρίνετε· δεν χρεώθηκε τίποτα και δεν προστέθηκαν μονάδες ακόμη.",
			CHARGE_AUTHENTICATED: "Η ταυτοποίηση ολοκληρώθηκε — οι μονάδες είναι στο υπόλοιπό σας.",
			NOT_WHOLE_BLOCKS: "Αυτή η προσθήκη πωλείται σε ακέραια πακέτα. Ζητήστε στρογγυλό αριθμό.",
			NO_SUCH_PACK: "Δεν υπάρχει πακέτο με αυτόν τον κωδικό.",
			PACK_ARCHIVED: "Αυτό το πακέτο δεν πωλείται πια.",
			PACK_NOT_PRICED: "Αυτό το πακέτο δεν πωλείται στο νόμισμά σου.",
			CANNOT_BUY_PACK: "Δεν ήταν δυνατή η αγορά του πακέτου.",
			CANNOT_CHOOSE_PLAN: "Δεν ήταν δυνατή η επιλογή αυτού του πλάνου.",
			NOT_FOR_SALE: "Αυτό δεν πωλείται με αυτοεξυπηρέτηση — μίλησε μαζί μας.",
			BELOW_USAGE: "Αυτό είναι κάτω από όσα ήδη χρησιμοποιεί αυτός ο χώρος εργασίας.",
			EXTRAS_CHANGE_FAILED: "Δεν ήταν δυνατή η αλλαγή των πρόσθετων.",
			NO_SUBSCRIPTION: "Αυτός ο χώρος εργασίας δεν έχει συνδρομή.",
			INVALID_REFERENCE_MONTH: "Ο μήνας αναφοράς είναι ΕΕΕΕ-ΜΜ.",
			NO_INVOICE: "Δεν υπάρχει τιμολόγιο για αυτόν τον μήνα.",
		},
		errorKind: {
			unauthorized: "Η συνεδρία σου έληξε. Συνδέσου ξανά.",
			forbidden: "Δεν έχεις δικαίωμα να το κάνεις αυτό.",
			notFound: "Αυτό δεν είναι πια εδώ.",
			validation: "Κάποια πεδία χρειάζονται διόρθωση.",
			conflict: "Αυτό έρχεται σε σύγκρουση με κάτι που υπάρχει ήδη.",
			rateLimited: "Πάρα πολλά αιτήματα. Περίμενε λίγο.",
			paymentRequired: "Αυτή η πόρτα είναι κλειστή σε αυτό το πλάνο.",
			server: "Κάτι πήγε στραβά από την πλευρά μας.",
			network: "Δεν ήταν δυνατή η επικοινωνία με τον διακομιστή.",
			timeout: "Ο διακομιστής άργησε πολύ να απαντήσει.",
		},
	},
	es: {
		error: {
			generic: "No se pudo terminar. Inténtalo de nuevo.",

			INVALID_CREDENTIALS: "Ese correo y esa contraseña no coinciden.",
			TOO_MANY_ATTEMPTS: "Demasiados intentos. Espera un momento e inténtalo de nuevo.",
			WRONG_PASSWORD: "Tu contraseña actual no coincide.",
			WEAK_PASSWORD: "La contraseña nueva necesita al menos 12 caracteres.",
			INVALID_REFRESH: "Tu sesión ha caducado. Inicia sesión de nuevo.",
			NO_SESSION: "Tu sesión ha caducado. Inicia sesión de nuevo.",
			NO_ENTITY:
				"Esta lista no existe o no está habilitada para usted. Pídala a un admin si la necesita.",
			LAST_OWNER:
				"Este espacio de trabajo necesita al menos un propietario activo. Asciende antes a otra persona.",

			SUBSCRIPTION_EXPIRED:
				"Tu prueba terminó. Elige un plan para reactivar este espacio de trabajo.",
			WORKSPACE_SUSPENDED: "Este espacio de trabajo está suspendido.",
			NEEDS_DATA_PLAN: "Tu plan no incluye esto.",
			ONBOARDING_BLOCKED: "Termina primero de poner en marcha el espacio de trabajo.",

			VALIDATION_ERROR: "Algunos campos necesitan un ajuste.",
			REQUIRED_FIELD: "Hay un campo obligatorio vacío.",
			WINDOW_TAKEN: "Ese horario ya está ocupado. Elige otro.",
			INSUFFICIENT_BALANCE: "No hay saldo suficiente para esto: quedaría por debajo del mínimo.",
			CAPACITY_FULL: "Ese horario está completo: no quedan plazas suficientes.",
			QUOTA_FULL: "El límite de este grupo ya se alcanzó.",
			INVALID_VALUE: "Ese valor no se acepta aquí.",
			VALUE_TOO_LONG: "Ese valor es demasiado largo.",
			VALUE_OUT_OF_RANGE: "Ese valor está fuera del rango permitido.",
			INVALID_DATE: "Esa fecha no existe o tiene un formato incorrecto.",
			INVALID_EMAIL: "Ese correo electrónico no es válido.",
			INVALID_URL: "Ese enlace no es válido — debe empezar con http:// o https://.",
			INVALID_DOCUMENT: "Ese número de documento no es válido — revisa los dígitos.",
			FILE_NOT_FOUND: "Ese archivo no existe en este espacio de trabajo — vuelve a adjuntarlo.",
			INVALID_PHONE: "Ese teléfono no existe — revisa el país y los dígitos.",
			INVALID_FILE: "Eso no es un archivo de este espacio de trabajo — vuelve a adjuntarlo.",
			INVALID_OPTION: "Esa opción no está en la lista de este campo.",
			SCHEDULE_IN_PAST: "Ese momento ya pasó — elige una fecha y hora futuras.",
			INVALID_INPUT: "Uno de los valores no tiene el formato esperado — revisa la fecha.",
			REFERENCE_NOT_FOUND: "Ese registro vinculado no existe o fue eliminado — elige otro.",
			INVALID_INTERVAL: "El fin está antes del inicio — revisa los dos horarios.",
			HAS_CHILDREN: "Otros registros aún dependen de este — elimínalos o muévelos antes.",
			EXPECTED_INTEGER: "Debe ser un número entero.",
			received: "{sentence} Recibió «{value}».",
			EXPECTED_NUMBER: "Debe ser un número.",
			EXPECTED_BOOLEAN: "Debe ser sí o no.",
			EXPECTED_TIME: "Debe ser una hora en el formato HH:MM.",
			VALUE_NOT_ALLOWED: "Este campo no acepta ese valor: revisa sus opciones o su formato.",
			UNKNOWN_FIELD: "Ese campo no existe aquí.",
			INVARIANT_VIOLATED: "Eso rompería una regla que este registro tiene que cumplir.",
			CONSTRAINT_VIOLATION: "La base de datos lo rechazó: rompería una regla.",
			ALREADY_EXISTS: "Eso ya existe.",
			CODE_TAKEN: "Ese código ya está en uso.",
			NOTHING_TO_UPDATE: "No se envió nada que cambiar.",
			NEEDS_CONFIRMATION: "Esto necesita confirmarse antes de ejecutarse.",

			CONTENTION: "Otra persona lo cambió antes. Recarga e inténtalo de nuevo.",
			BUSY: "Todavía estoy con el anterior. Inténtalo dentro de un momento.",
			TIMEOUT: "Eso tardó demasiado y se detuvo.",
			UNAVAILABLE: "Ese servicio no responde ahora mismo.",
			INTERNAL_ERROR: "Algo salió mal por nuestra parte.",
			UPLOAD_REFUSED: "Ese archivo fue rechazado.",
			SEND_FAILED: "No se pudo enviar.",

			PIPELINE_NOT_FOUND: "Ese embudo no existe.",
			STAGE_NOT_FOUND: "Ese embudo no tiene esa etapa.",
			PIPELINE_HAS_NO_STAGES:
				"Ese embudo todavía no tiene etapas — crea la primera y la tarjeta tendrá dónde entrar.",
			CONTACT_NOT_FOUND: "Esa persona no existe.",
			ALREADY_ON_FUNNEL: "Este contacto ya está en ese embudo.",
			CANNOT_PLACE: "No se pudo poner eso en el embudo.",
			INVALID_VALUE_FIELD: "Ese no es un campo numérico activo de esta pantalla.",

			UNKNOWN_PIPELINE: "Ese embudo no existe.",
			UNKNOWN_STAGE: "Ese embudo no tiene esa etapa.",
			NOT_CONTACT_CAPABLE:
				"Ese embudo no recibe personas: su pantalla no tiene un campo que apunte al contacto (tipo ref_contact), y un canal solo sabe poner en el embudo a quien escribió. Dale a la pantalla un campo de ese tipo — o enlaza el canal al embudo que acompaña a la persona.",
			EMPTY_PIPELINE: "Ese embudo todavía no tiene etapas — crea la primera y vuelve a enlazar.",
			UNKNOWN_CHANNEL: "No hay ningún canal de ese tipo.",
			RULE_GONE: "Esa regla dejó de existir.",
			HAND_WRITTEN:
				"Una regla escrita a mano ya lleva este canal a un embudo. El atajo no la toca — cámbiala o elimínala en Automatizaciones.",

			RECORD_LOCKED: "Este registro se firmó y ya no se puede cambiar.",
			DERIVED_FIELD:
				"Ese campo se calcula a partir de los registros enlazados y no se puede escribir.",
			NOT_APPLICABLE: "Esa acción no se aplica a este registro ahora mismo.",
			MISSING_PARAMETERS: "Faltan valores que esta acción necesita.",
			ROWS_WOULD_BREAK:
				"Algunos registros ya incumplen esta regla, así que no se pudo aplicar. Corrígelos primero.",
			MISSING_LABEL: "Un campo necesita una etiqueta.",
			INVALID_DISPLAY: "Un campo de ese tipo no se puede mostrar así.",
			UNKNOWN_ENTITY: "Esa pantalla no existe.",
			ENTITY_EXISTS: "Ya existe una pantalla con ese código. Elige otro nombre.",
			FIELD_EXISTS: "Esta pantalla ya tiene un campo con ese código.",
			RELATION_EXISTS: "Estas dos pantallas ya están enlazadas.",
			VIEW_EXISTS: "Esta pantalla ya tiene una vista con ese código.",
			SLUG_TAKEN: "Esa dirección ya la usa otra vista.",
			PUBLIC_FORM_MISSING_REQUIRED:
				"Un formulario público debe preguntar todos los campos obligatorios — añade los que faltan antes de publicar.",

			NOT_FOUND: "Eso ya no está aquí.",
			ALREADY_DECIDED: "Otra persona ya decidió esta.",
			NOT_A_COMMENT: "Ese mensaje no vino de un comentario.",
			CHANNEL_GONE: "El canal de esta conversación ya no existe.",
			MODERATION_UNSUPPORTED: "Este canal no modera comentarios.",
			PROVIDER_REFUSED: "El proveedor lo rechazó.",
			CHANNEL_LIMIT: "Tu plan no incluye otro canal.",
			CONTACT_LIMIT:
				"Este contacto supera el límite de contactos activos del plan. Los mensajes se guardan y se abren cuando el plan tenga margen.",
			STORAGE_LIMIT: "El almacenamiento del plan está lleno. No se subió nada.",
			EXCHANGE_FAILED:
				"No se pudo completar con Meta. Empieza el proceso otra vez — el código solo vale una vez.",
			MISSING_SCOPES:
				"La autorización no incluyó los permisos de WhatsApp — comprueba que la app use Embedded Signup.",
			NOTHING_FOUND:
				"No se encontró ninguna cuenta de WhatsApp Business con número en esa autorización.",
			NO_TOKEN: "Este canal no tiene guardado ningún token de Meta.",
			DO_NOT_CONTACT: "Este contacto pidió que no se le contacte.",

			NO_SUCH_PLAN: "No existe ningún plan con ese código.",
			PLAN_ARCHIVED: "Ese plan ya no está a la venta.",
			TRIAL_ALREADY_USED: "La prueba es una entrada única — elige un plan para continuar.",
			CHECKOUT_UNAVAILABLE:
				"La pasarela de pago no abrió el checkout. Nada cambió — inténtalo de nuevo en un momento.",
			GATEWAY_UNAVAILABLE:
				"Los planes de pago se cobran con tarjeta, y el pago con tarjeta no está activado en esta instalación. Nada cambió.",
			NO_PLAN: "Elige un plan antes de continuar.",
			PAID_NEEDS_CHECKOUT:
				"Un plan de pago solo empieza por el checkout del cliente; desde aquí solo se ponen planes gratuitos. Nada cambió.",
			USER_LIMIT: "Este plan no tiene plaza libre para otro miembro activo.",
			TRIAL_RAIL:
				"Los paquetes son para planes de pago, y una prueba todavía no lo es. Elige un plan y los paquetes se abren.",
			// Troca de plano para o plano de TESTE (409, 27/09/2026).
			TRIAL_TARGET:
				"La prueba es solo para empezar: no puede ser el plan de destino de un cambio. Elija uno de los planes de pago.",
			SUBSCRIPTION_CANCELLED: "Esta suscripción está cancelada.",
			NO_CARD:
				"Los paquetes se cobran en la tarjeta registrada, y esta suscripción no tiene ninguna. Registra una tarjeta e inténtalo de nuevo.",
			CHARGE_DECLINED:
				"La tarjeta fue rechazada. No se cobró nada ni entraron créditos — revise la tarjeta o use otra.",
			CHARGE_REQUIRES_ACTION:
				"Su banco pidió confirmar esta compra. Queda en espera hasta que la autorice; no se cobró nada ni entraron créditos todavía.",
			CHARGE_AUTHENTICATED: "Autenticación completada — los créditos ya están en su saldo.",
			NOT_WHOLE_BLOCKS:
				"Este adicional se vende en bloques enteros. Pida un número redondo de ellos.",
			NO_SUCH_PACK: "No existe ningún paquete con ese código.",
			PACK_ARCHIVED: "Ese paquete ya no está a la venta.",
			PACK_NOT_PRICED: "Ese paquete no está a la venta en tu moneda.",
			CANNOT_BUY_PACK: "No se pudo comprar el paquete.",
			CANNOT_CHOOSE_PLAN: "No se pudo elegir ese plan.",
			NOT_FOR_SALE: "Esto no se vende en autoservicio — habla con nosotros.",
			BELOW_USAGE: "Eso está por debajo de lo que este espacio de trabajo ya está usando.",
			EXTRAS_CHANGE_FAILED: "No se pudieron cambiar los complementos.",
			NO_SUBSCRIPTION: "Este espacio de trabajo no tiene suscripción.",
			INVALID_REFERENCE_MONTH: "El mes de referencia es AAAA-MM.",
			NO_INVOICE: "No hay factura de ese mes.",
		},
		errorKind: {
			unauthorized: "Tu sesión ha caducado. Inicia sesión de nuevo.",
			forbidden: "No tienes permiso para hacer eso.",
			notFound: "Eso ya no está aquí.",
			validation: "Algunos campos necesitan un ajuste.",
			conflict: "Eso choca con algo que ya existe.",
			rateLimited: "Demasiadas peticiones. Espera un momento.",
			paymentRequired: "Esta puerta está cerrada en este plan.",
			server: "Algo salió mal por nuestra parte.",
			network: "No se pudo contactar con el servidor.",
			timeout: "El servidor tardó demasiado en responder.",
		},
	},
	fr: {
		error: {
			generic: "Impossible de terminer. Réessayez.",

			INVALID_CREDENTIALS: "Cet e-mail et ce mot de passe ne correspondent pas.",
			TOO_MANY_ATTEMPTS: "Trop de tentatives. Attendez un instant et réessayez.",
			WRONG_PASSWORD: "Votre mot de passe actuel ne correspond pas.",
			WEAK_PASSWORD: "Le nouveau mot de passe doit faire au moins 12 caractères.",
			INVALID_REFRESH: "Votre session a expiré. Reconnectez-vous.",
			NO_SESSION: "Votre session a expiré. Reconnectez-vous.",
			NO_ENTITY:
				"Cette liste n'existe pas ou ne vous est pas ouverte. Demandez-la à un admin si vous en avez besoin.",
			LAST_OWNER:
				"Cet espace de travail a besoin d'au moins un propriétaire actif. Promouvez d'abord quelqu'un d'autre.",

			SUBSCRIPTION_EXPIRED:
				"Votre essai est terminé. Choisissez un forfait pour réactiver cet espace de travail.",
			WORKSPACE_SUSPENDED: "Cet espace de travail est suspendu.",
			NEEDS_DATA_PLAN: "Votre forfait ne comprend pas cela.",
			ONBOARDING_BLOCKED: "Terminez d'abord la prise en main de l'espace de travail.",

			VALIDATION_ERROR: "Certains champs sont à corriger.",
			REQUIRED_FIELD: "Un champ obligatoire est vide.",
			WINDOW_TAKEN: "Ce créneau est déjà pris. Choisissez-en un autre.",
			INSUFFICIENT_BALANCE: "Le solde est insuffisant : il passerait sous le minimum.",
			CAPACITY_FULL: "Ce créneau est complet : il ne reste pas assez de places.",
			QUOTA_FULL: "La limite de ce groupe est déjà atteinte.",
			INVALID_VALUE: "Cette valeur n'est pas acceptée ici.",
			VALUE_TOO_LONG: "Cette valeur est trop longue.",
			VALUE_OUT_OF_RANGE: "Cette valeur est hors de la plage autorisée.",
			INVALID_DATE: "Cette date n’existe pas ou n’est pas au bon format.",
			INVALID_EMAIL: "Cette adresse e-mail n’est pas valide.",
			INVALID_URL: "Ce lien n’est pas valide — il doit commencer par http:// ou https://.",
			INVALID_DOCUMENT: "Ce numéro de document n’est pas valide — vérifiez les chiffres.",
			FILE_NOT_FOUND: "Ce fichier n’existe pas dans cet espace de travail — joignez-le à nouveau.",
			INVALID_PHONE: "Ce numéro n’existe pas — vérifiez le pays et les chiffres.",
			INVALID_FILE: "Ce n’est pas un fichier de cet espace de travail — joignez-le à nouveau.",
			INVALID_OPTION: "Cette option n’est pas dans la liste de ce champ.",
			SCHEDULE_IN_PAST: "Ce moment est déjà passé — choisissez une date et une heure futures.",
			INVALID_INPUT: "Une des valeurs n’a pas le format attendu — vérifiez la date.",
			REFERENCE_NOT_FOUND:
				"Cette fiche liée n’existe pas ou a été supprimée — choisissez-en une autre.",
			INVALID_INTERVAL: "La fin est avant le début — vérifiez les deux horaires.",
			HAS_CHILDREN:
				"D’autres fiches dépendent encore de celle-ci — supprimez-les ou déplacez-les d’abord.",
			EXPECTED_INTEGER: "Doit être un nombre entier.",
			received: "{sentence} Reçu « {value} ».",
			EXPECTED_NUMBER: "Doit être un nombre.",
			EXPECTED_BOOLEAN: "Doit être oui ou non.",
			EXPECTED_TIME: "Doit être une heure au format HH:MM.",
			VALUE_NOT_ALLOWED:
				"Ce champ n’accepte pas cette valeur : vérifiez ses options ou son format.",
			UNKNOWN_FIELD: "Ce champ n'existe pas ici.",
			INVARIANT_VIOLATED: "Cela romprait une règle que cette fiche doit respecter.",
			CONSTRAINT_VIOLATION: "La base de données a refusé : cela romprait une règle.",
			ALREADY_EXISTS: "Cela existe déjà.",
			CODE_TAKEN: "Ce code est déjà utilisé.",
			NOTHING_TO_UPDATE: "Rien n'a été envoyé à modifier.",
			NEEDS_CONFIRMATION: "Cela doit être confirmé avant de s'exécuter.",

			CONTENTION: "Quelqu'un d'autre l'a modifié avant vous. Rechargez et réessayez.",
			BUSY: "Le précédent n'est pas terminé. Réessayez dans un instant.",
			TIMEOUT: "Cela a pris trop de temps et a été interrompu.",
			UNAVAILABLE: "Ce service ne répond pas pour le moment.",
			INTERNAL_ERROR: "Quelque chose a mal tourné de notre côté.",
			UPLOAD_REFUSED: "Ce fichier a été refusé.",
			SEND_FAILED: "L'envoi n'a pas pu se faire.",

			PIPELINE_NOT_FOUND: "Ce pipeline n'existe pas.",
			STAGE_NOT_FOUND: "Ce pipeline n'a pas cette étape.",
			PIPELINE_HAS_NO_STAGES:
				"Ce pipeline n'a encore aucune étape — créez la première et la carte aura où aller.",
			CONTACT_NOT_FOUND: "Cette personne n'existe pas.",
			ALREADY_ON_FUNNEL: "Ce contact est déjà sur ce pipeline.",
			CANNOT_PLACE: "Impossible de le placer sur le pipeline.",
			INVALID_VALUE_FIELD: "Ce n'est pas un champ numérique actif de cet écran.",

			UNKNOWN_PIPELINE: "Ce pipeline n'existe pas.",
			UNKNOWN_STAGE: "Ce pipeline n'a pas cette étape.",
			NOT_CONTACT_CAPABLE:
				"Ce pipeline ne reçoit pas de personnes : son écran n'a aucun champ pointant vers le contact (type ref_contact), et un canal ne sait y placer que celui qui a écrit. Donnez à l'écran un champ de ce type — ou liez le canal au pipeline qui suit la personne.",
			EMPTY_PIPELINE:
				"Ce pipeline n'a encore aucune étape — créez la première et reliez à nouveau.",
			UNKNOWN_CHANNEL: "Il n'existe aucun canal de ce type.",
			RULE_GONE: "Cette règle a cessé d'exister.",
			HAND_WRITTEN:
				"Une règle écrite à la main envoie déjà ce canal vers un pipeline. Le raccourci n'y touche pas — modifiez-la ou supprimez-la dans Automatisations.",

			RECORD_LOCKED: "Cette fiche a été signée et ne peut plus être modifiée.",
			DERIVED_FIELD: "Ce champ est calculé à partir des fiches liées et ne se saisit pas.",
			NOT_APPLICABLE: "Cette action ne s'applique pas à cette fiche pour le moment.",
			MISSING_PARAMETERS: "Des valeurs dont cette action a besoin n'ont pas été remplies.",
			ROWS_WOULD_BREAK:
				"Certaines fiches enfreignent déjà cette règle, elle n'a donc pas pu être appliquée. Corrigez-les d'abord.",
			MISSING_LABEL: "Un champ a besoin d'un libellé.",
			INVALID_DISPLAY: "Un champ de ce type ne peut pas être affiché ainsi.",
			UNKNOWN_ENTITY: "Cet écran n'existe pas.",
			ENTITY_EXISTS: "Un écran avec ce code existe déjà. Choisissez un autre nom.",
			FIELD_EXISTS: "Cet écran a déjà un champ avec ce code.",
			RELATION_EXISTS: "Ces deux écrans sont déjà liés.",
			VIEW_EXISTS: "Cet écran a déjà une vue avec ce code.",
			SLUG_TAKEN: "Cette adresse est déjà prise par une autre vue.",
			PUBLIC_FORM_MISSING_REQUIRED:
				"Un formulaire public doit poser tous les champs obligatoires — ajoutez ceux qui manquent avant de publier.",

			NOT_FOUND: "Ce n'est plus ici.",
			ALREADY_DECIDED: "Quelqu'un d'autre a déjà tranché celle-ci.",
			NOT_A_COMMENT: "Ce message ne vient pas d'un commentaire.",
			CHANNEL_GONE: "Le canal derrière cette conversation n'existe plus.",
			MODERATION_UNSUPPORTED: "Ce canal ne modère pas les commentaires.",
			PROVIDER_REFUSED: "Le fournisseur a refusé.",
			CHANNEL_LIMIT: "Votre forfait ne comprend pas un canal de plus.",
			CONTACT_LIMIT:
				"Ce contact dépasse la limite de contacts actifs du forfait. Ses messages sont conservés et s'ouvriront quand le forfait aura de la marge.",
			STORAGE_LIMIT: "Le stockage du forfait est plein. Rien n'a été envoyé.",
			EXCHANGE_FAILED:
				"Impossible de terminer avec Meta. Recommencez la procédure — le code ne vaut qu'une fois.",
			MISSING_SCOPES:
				"L'autorisation n'incluait pas les permissions WhatsApp — vérifiez que l'app utilise Embedded Signup.",
			NOTHING_FOUND:
				"Aucun compte WhatsApp Business avec un numéro n'a été trouvé dans cette autorisation.",
			NO_TOKEN: "Ce canal n'a aucun jeton Meta enregistré.",
			DO_NOT_CONTACT: "Ce contact a demandé à ne pas être démarché.",

			NO_SUCH_PLAN: "Il n'existe aucun forfait avec ce code.",
			PLAN_ARCHIVED: "Ce forfait n'est plus commercialisé.",
			TRIAL_ALREADY_USED: "L'essai ne se prend qu'une fois — choisissez un forfait pour continuer.",
			CHECKOUT_UNAVAILABLE:
				"La passerelle de paiement n'a pas ouvert le paiement. Rien n'a changé — réessayez dans un instant.",
			GATEWAY_UNAVAILABLE:
				"Les forfaits payants sont prélevés par carte, et le paiement par carte n'est pas activé sur cette installation. Rien n'a changé.",
			NO_PLAN: "Choisissez un forfait avant de continuer.",
			PAID_NEEDS_CHECKOUT:
				"Un forfait payant ne démarre que par le paiement du client ; d'ici, seuls les forfaits gratuits. Rien n'a changé.",
			USER_LIMIT: "Ce forfait n'a plus de place pour un membre actif de plus.",
			TRIAL_RAIL:
				"Les packs sont pour les forfaits payants, et un essai n'en est pas encore un. Choisissez un forfait et les packs s'ouvrent.",
			// Troca de plano para o plano de TESTE (409, 27/09/2026).
			TRIAL_TARGET:
				"L'essai sert seulement à démarrer : il ne peut pas être le forfait visé par un changement. Choisissez un des forfaits payants.",
			SUBSCRIPTION_CANCELLED: "Cet abonnement est résilié.",
			NO_CARD:
				"Les packs sont prélevés sur la carte enregistrée, et cet abonnement n'en a pas. Ajoutez une carte et réessayez.",
			CHARGE_DECLINED:
				"La carte a été refusée. Rien n'a été débité et aucun crédit n'est entré — vérifiez la carte ou utilisez-en une autre.",
			CHARGE_REQUIRES_ACTION:
				"Votre banque a demandé de confirmer cet achat. Il reste en attente jusqu'à votre autorisation ; rien n'a été débité et aucun crédit n'est encore entré.",
			CHARGE_AUTHENTICATED: "Authentification terminée — les crédits sont sur votre solde.",
			NOT_WHOLE_BLOCKS: "Cette option se vend par tranches entières. Demandez-en un nombre rond.",
			NO_SUCH_PACK: "Il n'existe aucun pack avec ce code.",
			PACK_ARCHIVED: "Ce pack n'est plus commercialisé.",
			PACK_NOT_PRICED: "Ce pack n'est pas vendu dans votre devise.",
			CANNOT_BUY_PACK: "Le pack n'a pas pu être acheté.",
			CANNOT_CHOOSE_PLAN: "Ce forfait n'a pas pu être choisi.",
			NOT_FOR_SALE: "Cela ne se vend pas en libre-service — parlez-nous.",
			BELOW_USAGE: "C'est en dessous de ce que cet espace de travail utilise déjà.",
			EXTRAS_CHANGE_FAILED: "Les options supplémentaires n'ont pas pu être modifiées.",
			NO_SUBSCRIPTION: "Cet espace de travail n'a pas d'abonnement.",
			INVALID_REFERENCE_MONTH: "Le mois de référence s'écrit AAAA-MM.",
			NO_INVOICE: "Il n'y a pas de facture pour ce mois.",
		},
		errorKind: {
			unauthorized: "Votre session a expiré. Reconnectez-vous.",
			forbidden: "Vous n'avez pas la permission de faire cela.",
			notFound: "Ce n'est plus ici.",
			validation: "Certains champs sont à corriger.",
			conflict: "Cela entre en conflit avec quelque chose qui existe déjà.",
			rateLimited: "Trop de requêtes. Attendez un instant.",
			paymentRequired: "Cette porte est fermée dans ce forfait.",
			server: "Quelque chose a mal tourné de notre côté.",
			network: "Impossible de joindre le serveur.",
			timeout: "Le serveur a mis trop de temps à répondre.",
		},
	},
	it: {
		error: {
			generic: "Non è stato possibile completare. Riprova.",

			INVALID_CREDENTIALS: "Questa email e questa password non corrispondono.",
			TOO_MANY_ATTEMPTS: "Troppi tentativi. Aspetta un momento e riprova.",
			WRONG_PASSWORD: "La tua password attuale non corrisponde.",
			WEAK_PASSWORD: "La nuova password deve avere almeno 12 caratteri.",
			INVALID_REFRESH: "La tua sessione è scaduta. Accedi di nuovo.",
			NO_SESSION: "La tua sessione è scaduta. Accedi di nuovo.",
			NO_ENTITY: "Questo elenco non esiste o non è aperto per te. Chiedilo a un admin se ti serve.",
			LAST_OWNER:
				"Questo spazio di lavoro ha bisogno di almeno un proprietario attivo. Promuovi prima qualcun altro.",

			SUBSCRIPTION_EXPIRED:
				"La tua prova è finita. Scegli un piano per riattivare questo spazio di lavoro.",
			WORKSPACE_SUSPENDED: "Questo spazio di lavoro è sospeso.",
			NEEDS_DATA_PLAN: "Il tuo piano non lo include.",
			ONBOARDING_BLOCKED: "Completa prima l'onboarding dello spazio di lavoro.",

			VALIDATION_ERROR: "Alcuni campi vanno sistemati.",
			REQUIRED_FIELD: "Un campo obbligatorio è vuoto.",
			WINDOW_TAKEN: "Questo orario è già occupato. Scegline un altro.",
			INSUFFICIENT_BALANCE: "Il saldo non basta: scenderebbe sotto il minimo.",
			CAPACITY_FULL: "Questo orario è al completo: non ci sono abbastanza posti.",
			QUOTA_FULL: "Il limite di questo gruppo è già stato raggiunto.",
			INVALID_VALUE: "Questo valore non è accettato qui.",
			VALUE_TOO_LONG: "Questo valore è troppo lungo.",
			VALUE_OUT_OF_RANGE: "Questo valore è fuori dall'intervallo consentito.",
			INVALID_DATE: "Questa data non esiste o è nel formato sbagliato.",
			INVALID_EMAIL: "Questa e-mail non è valida.",
			INVALID_URL: "Questo link non è valido — deve iniziare con http:// o https://.",
			INVALID_DOCUMENT: "Questo numero di documento non è valido — controlla le cifre.",
			FILE_NOT_FOUND: "Questo file non esiste in questo workspace — allegalo di nuovo.",
			INVALID_PHONE: "Questo telefono non esiste — controlla il paese e le cifre.",
			INVALID_FILE: "Non è un file di questo workspace — allegalo di nuovo.",
			INVALID_OPTION: "Questa opzione non è nell’elenco di questo campo.",
			SCHEDULE_IN_PAST: "Quel momento è già passato — scegli una data e un’ora future.",
			INVALID_INPUT: "Uno dei valori non è nel formato previsto — controlla la data.",
			REFERENCE_NOT_FOUND:
				"Questo record collegato non esiste o è stato eliminato — scegline un altro.",
			INVALID_INTERVAL: "La fine viene prima dell’inizio — controlla i due orari.",
			HAS_CHILDREN: "Altri record dipendono ancora da questo — eliminali o spostali prima.",
			EXPECTED_INTEGER: "Deve essere un numero intero.",
			received: "{sentence} Ricevuto «{value}».",
			EXPECTED_NUMBER: "Deve essere un numero.",
			EXPECTED_BOOLEAN: "Deve essere sì o no.",
			EXPECTED_TIME: "Deve essere un orario nel formato HH:MM.",
			VALUE_NOT_ALLOWED:
				"Questo campo non accetta quel valore: controlla le sue opzioni o il formato.",
			UNKNOWN_FIELD: "Questo campo qui non esiste.",
			INVARIANT_VIOLATED: "Questo romperebbe una regola che il record deve rispettare.",
			CONSTRAINT_VIOLATION: "Il database ha rifiutato: romperebbe una regola.",
			ALREADY_EXISTS: "Esiste già.",
			CODE_TAKEN: "Questo codice è già in uso.",
			NOTHING_TO_UPDATE: "Non è stato inviato nulla da cambiare.",
			NEEDS_CONFIRMATION: "Va confermato prima di essere eseguito.",

			CONTENTION: "Qualcun altro l'ha cambiato prima. Ricarica e riprova.",
			BUSY: "Sto ancora finendo il precedente. Riprova tra un momento.",
			TIMEOUT: "Ci ha messo troppo ed è stato interrotto.",
			UNAVAILABLE: "Questo servizio non risponde in questo momento.",
			INTERNAL_ERROR: "Qualcosa è andato storto da parte nostra.",
			UPLOAD_REFUSED: "Questo file è stato rifiutato.",
			SEND_FAILED: "Non è stato possibile inviarlo.",

			PIPELINE_NOT_FOUND: "Questa pipeline non esiste.",
			STAGE_NOT_FOUND: "Questa pipeline non ha quella fase.",
			PIPELINE_HAS_NO_STAGES:
				"Questa pipeline non ha ancora fasi — crea la prima e la scheda avrà dove andare.",
			CONTACT_NOT_FOUND: "Questa persona non esiste.",
			ALREADY_ON_FUNNEL: "Questo contatto è già in quella pipeline.",
			CANNOT_PLACE: "Non è stato possibile metterlo nella pipeline.",
			INVALID_VALUE_FIELD: "Non è un campo numerico attivo di questa schermata.",

			UNKNOWN_PIPELINE: "Questa pipeline non esiste.",
			UNKNOWN_STAGE: "Questa pipeline non ha quella fase.",
			NOT_CONTACT_CAPABLE:
				"Questa pipeline non riceve persone: la sua schermata non ha un campo che punti al contatto (tipo ref_contact), e un canale sa metterci solo chi ha scritto. Dai alla schermata un campo di quel tipo — o collega il canale alla pipeline che segue la persona.",
			EMPTY_PIPELINE: "Questa pipeline non ha ancora fasi — crea la prima e collega di nuovo.",
			UNKNOWN_CHANNEL: "Non esiste un canale di quel tipo.",
			RULE_GONE: "Questa regola ha smesso di esistere.",
			HAND_WRITTEN:
				"Una regola scritta a mano porta già questo canale in una pipeline. La scorciatoia non la tocca — modificala o eliminala in Automazioni.",

			RECORD_LOCKED: "Questo record è stato firmato e non può più essere modificato.",
			DERIVED_FIELD: "Questo campo è calcolato dai record collegati e non si può scrivere.",
			NOT_APPLICABLE: "Questa azione non si applica a questo record al momento.",
			MISSING_PARAMETERS: "Mancano dei valori che questa azione richiede.",
			ROWS_WOULD_BREAK:
				"Alcuni record violano già questa regola, quindi non è stato possibile applicarla. Sistemali prima.",
			MISSING_LABEL: "Un campo ha bisogno di un'etichetta.",
			INVALID_DISPLAY: "Un campo di quel tipo non si può mostrare così.",
			UNKNOWN_ENTITY: "Questa schermata non esiste.",
			ENTITY_EXISTS: "Esiste già una schermata con questo codice. Scegli un altro nome.",
			FIELD_EXISTS: "Questa schermata ha già un campo con questo codice.",
			RELATION_EXISTS: "Queste due schermate sono già collegate.",
			VIEW_EXISTS: "Questa schermata ha già una vista con questo codice.",
			SLUG_TAKEN: "Questo indirizzo è già usato da un’altra vista.",
			PUBLIC_FORM_MISSING_REQUIRED:
				"Un modulo pubblico deve chiedere tutti i campi obbligatori — aggiungi quelli mancanti prima di pubblicare.",

			NOT_FOUND: "Non è più qui.",
			ALREADY_DECIDED: "Qualcun altro ha già deciso su questa.",
			NOT_A_COMMENT: "Questo messaggio non arriva da un commento.",
			CHANNEL_GONE: "Il canale dietro questa conversazione non esiste più.",
			MODERATION_UNSUPPORTED: "Questo canale non modera i commenti.",
			PROVIDER_REFUSED: "Il provider ha rifiutato.",
			CHANNEL_LIMIT: "Il tuo piano non include un altro canale.",
			CONTACT_LIMIT:
				"Questo contatto è oltre il limite di contatti attivi del piano. I messaggi sono conservati e si aprono quando il piano ha margine.",
			STORAGE_LIMIT: "Lo spazio di archiviazione del piano è pieno. Non è stato caricato nulla.",
			EXCHANGE_FAILED:
				"Non è stato possibile completare con Meta. Ricomincia la procedura — il codice vale una volta sola.",
			MISSING_SCOPES:
				"L'autorizzazione non includeva i permessi di WhatsApp — controlla che l'app usi Embedded Signup.",
			NOTHING_FOUND:
				"In quell'autorizzazione non è stato trovato nessun account WhatsApp Business con un numero.",
			NO_TOKEN: "Questo canale non ha un token Meta salvato.",
			DO_NOT_CONTACT: "Questo contatto ha chiesto di non essere contattato.",

			NO_SUCH_PLAN: "Non esiste un piano con quel codice.",
			PLAN_ARCHIVED: "Questo piano non è più in vendita.",
			TRIAL_ALREADY_USED: "La prova è un ingresso unico — scegli un piano per andare avanti.",
			CHECKOUT_UNAVAILABLE:
				"Il gateway di pagamento non ha aperto il checkout. Nulla è cambiato — riprova tra un istante.",
			GATEWAY_UNAVAILABLE:
				"I piani a pagamento si addebitano sulla carta, e il pagamento con carta non è attivo in questa installazione. Nulla è cambiato.",
			NO_PLAN: "Scegli un piano prima di andare avanti.",
			PAID_NEEDS_CHECKOUT:
				"Un piano a pagamento parte solo dal checkout del cliente; da qui solo piani gratuiti. Nulla è cambiato.",
			USER_LIMIT: "Questo piano non ha più posto per un altro membro attivo.",
			TRIAL_RAIL:
				"I pacchetti sono per i piani a pagamento, e una prova non lo è ancora. Scegli un piano e i pacchetti si sbloccano.",
			// Troca de plano para o plano de TESTE (409, 27/09/2026).
			TRIAL_TARGET:
				"La prova serve solo per iniziare: non può essere il piano di destinazione di un cambio. Scegli uno dei piani a pagamento.",
			SUBSCRIPTION_CANCELLED: "Questo abbonamento è annullato.",
			NO_CARD:
				"I pacchetti si addebitano sulla carta registrata, e questo abbonamento non ne ha. Aggiungi una carta e riprova.",
			CHARGE_DECLINED:
				"La carta è stata rifiutata. Non è stato addebitato nulla e non sono entrati crediti — controlla la carta o usane un'altra.",
			CHARGE_REQUIRES_ACTION:
				"La tua banca ha chiesto di confermare questo acquisto. Resta in attesa finché non lo autorizzi; non è stato addebitato nulla e non sono ancora entrati crediti.",
			CHARGE_AUTHENTICATED: "Autenticazione completata — i crediti sono nel tuo saldo.",
			NOT_WHOLE_BLOCKS: "Questo componente si vende a blocchi interi. Chiedine un numero tondo.",
			NO_SUCH_PACK: "Non esiste un pacchetto con quel codice.",
			PACK_ARCHIVED: "Questo pacchetto non è più in vendita.",
			PACK_NOT_PRICED: "Questo pacchetto non è in vendita nella tua valuta.",
			CANNOT_BUY_PACK: "Non è stato possibile acquistare il pacchetto.",
			CANNOT_CHOOSE_PLAN: "Non è stato possibile scegliere quel piano.",
			NOT_FOR_SALE: "Questo non si vende in self-service — parlane con noi.",
			BELOW_USAGE: "È meno di quello che questo spazio di lavoro sta già usando.",
			EXTRAS_CHANGE_FAILED: "Non è stato possibile cambiare i componenti aggiuntivi.",
			NO_SUBSCRIPTION: "Questo spazio di lavoro non ha un abbonamento.",
			INVALID_REFERENCE_MONTH: "Il mese di riferimento è AAAA-MM.",
			NO_INVOICE: "Non c'è una fattura per quel mese.",
		},
		errorKind: {
			unauthorized: "La tua sessione è scaduta. Accedi di nuovo.",
			forbidden: "Non hai il permesso di farlo.",
			notFound: "Non è più qui.",
			validation: "Alcuni campi vanno sistemati.",
			conflict: "Va in conflitto con qualcosa che esiste già.",
			rateLimited: "Troppe richieste. Aspetta un momento.",
			paymentRequired: "Questa porta è chiusa in questo piano.",
			server: "Qualcosa è andato storto da parte nostra.",
			network: "Non è stato possibile raggiungere il server.",
			timeout: "Il server ha impiegato troppo a rispondere.",
		},
	},
	de: {
		error: {
			generic: "Hat nicht geklappt. Versuch es noch einmal.",

			INVALID_CREDENTIALS: "Diese E-Mail und dieses Passwort passen nicht zusammen.",
			TOO_MANY_ATTEMPTS: "Zu viele Versuche. Warte einen Moment und versuch es noch einmal.",
			WRONG_PASSWORD: "Dein aktuelles Passwort stimmt nicht.",
			WEAK_PASSWORD: "Das neue Passwort braucht mindestens 12 Zeichen.",
			INVALID_REFRESH: "Deine Sitzung ist abgelaufen. Melde dich neu an.",
			NO_SESSION: "Deine Sitzung ist abgelaufen. Melde dich neu an.",
			NO_ENTITY:
				"Diese Liste gibt es nicht oder sie ist für Sie nicht freigegeben. Fragen Sie einen Admin, wenn Sie sie brauchen.",
			LAST_OWNER:
				"Dieser Workspace braucht mindestens einen aktiven Inhaber. Befördere zuerst jemand anderen.",

			SUBSCRIPTION_EXPIRED:
				"Deine Testphase ist vorbei. Wähle einen Tarif, um diesen Workspace zu reaktivieren.",
			WORKSPACE_SUSPENDED: "Dieser Workspace ist gesperrt.",
			NEEDS_DATA_PLAN: "Dein Tarif enthält das nicht.",
			ONBOARDING_BLOCKED: "Richte den Workspace zuerst fertig ein.",

			VALIDATION_ERROR: "Einige Felder müssen korrigiert werden.",
			REQUIRED_FIELD: "Ein Pflichtfeld ist leer.",
			WINDOW_TAKEN: "Dieser Zeitraum ist bereits belegt. Wähle einen anderen.",
			INSUFFICIENT_BALANCE: "Der Saldo reicht nicht — er fiele unter das Minimum.",
			CAPACITY_FULL: "Dieser Zeitraum ist ausgebucht — es sind nicht genug Plätze frei.",
			QUOTA_FULL: "Das Limit dieser Gruppe ist bereits erreicht.",
			INVALID_VALUE: "Dieser Wert wird hier nicht angenommen.",
			VALUE_TOO_LONG: "Dieser Wert ist zu lang.",
			VALUE_OUT_OF_RANGE: "Dieser Wert liegt außerhalb des erlaubten Bereichs.",
			INVALID_DATE: "Dieses Datum gibt es nicht, oder es hat das falsche Format.",
			INVALID_EMAIL: "Diese E-Mail-Adresse ist ungültig.",
			INVALID_URL: "Dieser Link ist ungültig — er muss mit http:// oder https:// beginnen.",
			INVALID_DOCUMENT: "Diese Dokumentnummer ist ungültig — prüfen Sie die Ziffern.",
			FILE_NOT_FOUND: "Diese Datei gibt es in diesem Workspace nicht — hängen Sie sie erneut an.",
			INVALID_PHONE: "Diese Telefonnummer gibt es nicht — prüfen Sie Land und Ziffern.",
			INVALID_FILE: "Das ist keine Datei dieses Workspace — hängen Sie sie erneut an.",
			INVALID_OPTION: "Diese Option steht nicht in der Liste dieses Feldes.",
			SCHEDULE_IN_PAST:
				"Dieser Zeitpunkt ist schon vorbei — wählen Sie ein zukünftiges Datum und eine Uhrzeit.",
			INVALID_INPUT: "Einer der Werte hat nicht das erwartete Format — prüfen Sie das Datum.",
			REFERENCE_NOT_FOUND:
				"Dieser verknüpfte Datensatz existiert nicht oder wurde gelöscht — wählen Sie einen anderen.",
			INVALID_INTERVAL: "Das Ende liegt vor dem Beginn — prüfen Sie die beiden Zeiten.",
			HAS_CHILDREN:
				"Andere Datensätze hängen noch von diesem ab — löschen oder verschieben Sie sie zuerst.",
			EXPECTED_INTEGER: "Muss eine ganze Zahl sein.",
			received: "{sentence} Erhalten: „{value}“.",
			EXPECTED_NUMBER: "Muss eine Zahl sein.",
			EXPECTED_BOOLEAN: "Muss Ja oder Nein sein.",
			EXPECTED_TIME: "Muss eine Uhrzeit im Format HH:MM sein.",
			VALUE_NOT_ALLOWED:
				"Dieses Feld nimmt den Wert nicht an — prüfen Sie seine Optionen oder sein Format.",
			UNKNOWN_FIELD: "Dieses Feld gibt es hier nicht.",
			INVARIANT_VIOLATED: "Das würde eine Regel brechen, die dieser Datensatz einhalten muss.",
			CONSTRAINT_VIOLATION: "Die Datenbank hat abgelehnt: Das würde eine Regel brechen.",
			ALREADY_EXISTS: "Das gibt es schon.",
			CODE_TAKEN: "Dieser Code ist bereits vergeben.",
			NOTHING_TO_UPDATE: "Es wurde nichts zum Ändern geschickt.",
			NEEDS_CONFIRMATION: "Das muss bestätigt werden, bevor es läuft.",

			CONTENTION: "Jemand anderes war schneller. Lade neu und versuch es noch einmal.",
			BUSY: "Der letzte läuft noch. Versuch es gleich noch einmal.",
			TIMEOUT: "Das hat zu lange gedauert und wurde abgebrochen.",
			UNAVAILABLE: "Dieser Dienst antwortet gerade nicht.",
			INTERNAL_ERROR: "Auf unserer Seite ist etwas schiefgegangen.",
			UPLOAD_REFUSED: "Diese Datei wurde abgelehnt.",
			SEND_FAILED: "Das Senden hat nicht geklappt.",

			PIPELINE_NOT_FOUND: "Diese Pipeline gibt es nicht.",
			STAGE_NOT_FOUND: "Diese Pipeline hat diese Phase nicht.",
			PIPELINE_HAS_NO_STAGES:
				"Diese Pipeline hat noch keine Phasen — lege die erste an, dann hat die Karte einen Platz.",
			CONTACT_NOT_FOUND: "Diese Person gibt es nicht.",
			ALREADY_ON_FUNNEL: "Dieser Kontakt ist schon in dieser Pipeline.",
			CANNOT_PLACE: "Das konnte nicht in die Pipeline gelegt werden.",
			INVALID_VALUE_FIELD: "Das ist kein aktives Zahlenfeld dieser Maske.",

			UNKNOWN_PIPELINE: "Diese Pipeline gibt es nicht.",
			UNKNOWN_STAGE: "Diese Pipeline hat diese Phase nicht.",
			NOT_CONTACT_CAPABLE:
				"Diese Pipeline nimmt keine Personen auf: ihre Maske hat kein Feld, das auf den Kontakt zeigt (Typ ref_contact), und ein Kanal kann nur denjenigen hineinsetzen, der geschrieben hat. Gib der Maske ein solches Feld — oder verknüpfe den Kanal mit der Pipeline, die die Person begleitet.",
			EMPTY_PIPELINE:
				"Diese Pipeline hat noch keine Phasen — lege die erste an und verknüpfe erneut.",
			UNKNOWN_CHANNEL: "Es gibt keinen Kanal dieser Art.",
			RULE_GONE: "Diese Regel gibt es nicht mehr.",
			HAND_WRITTEN:
				"Eine von Hand geschriebene Regel schickt diesen Kanal schon in eine Pipeline. Die Abkürzung rührt sie nicht an — ändere oder lösche sie unter Automatisierungen.",

			RECORD_LOCKED: "Dieser Datensatz wurde unterschrieben und lässt sich nicht mehr ändern.",
			DERIVED_FIELD:
				"Dieses Feld wird aus verknüpften Datensätzen berechnet und lässt sich nicht eintippen.",
			NOT_APPLICABLE: "Diese Aktion passt gerade nicht zu diesem Datensatz.",
			MISSING_PARAMETERS: "Einige Werte, die diese Aktion braucht, wurden nicht ausgefüllt.",
			ROWS_WOULD_BREAK:
				"Einige Datensätze brechen diese Regel bereits, deshalb ließ sie sich nicht anwenden. Bring sie zuerst in Ordnung.",
			MISSING_LABEL: "Ein Feld braucht eine Beschriftung.",
			INVALID_DISPLAY: "Ein Feld dieser Art lässt sich so nicht anzeigen.",
			UNKNOWN_ENTITY: "Diese Maske gibt es nicht.",
			ENTITY_EXISTS: "Eine Maske mit diesem Code gibt es schon. Wählen Sie einen anderen Namen.",
			FIELD_EXISTS: "Diese Maske hat bereits ein Feld mit diesem Code.",
			RELATION_EXISTS: "Diese beiden Masken sind bereits verknüpft.",
			VIEW_EXISTS: "Diese Maske hat bereits eine Ansicht mit diesem Code.",
			SLUG_TAKEN: "Diese Adresse ist bereits von einer anderen Ansicht belegt.",
			PUBLIC_FORM_MISSING_REQUIRED:
				"Ein öffentliches Formular muss alle Pflichtfelder abfragen — fügen Sie die fehlenden vor dem Veröffentlichen hinzu.",

			NOT_FOUND: "Das ist nicht mehr hier.",
			ALREADY_DECIDED: "Jemand anderes hat das schon entschieden.",
			NOT_A_COMMENT: "Diese Nachricht kam nicht aus einem Kommentar.",
			CHANNEL_GONE: "Den Kanal hinter dieser Konversation gibt es nicht mehr.",
			MODERATION_UNSUPPORTED: "Dieser Kanal moderiert keine Kommentare.",
			PROVIDER_REFUSED: "Der Anbieter hat abgelehnt.",
			CHANNEL_LIMIT: "Dein Tarif enthält keinen weiteren Kanal.",
			CONTACT_LIMIT:
				"Dieser Kontakt liegt über dem Limit aktiver Kontakte des Tarifs. Die Nachrichten bleiben gespeichert und öffnen sich, sobald der Tarif Platz hat.",
			STORAGE_LIMIT: "Der Speicher des Tarifs ist voll. Es wurde nichts hochgeladen.",
			EXCHANGE_FAILED:
				"Das ließ sich mit Meta nicht abschließen. Starte den Vorgang neu — der Code gilt nur einmal.",
			MISSING_SCOPES:
				"Die Autorisierung enthielt die WhatsApp-Berechtigungen nicht — prüfe, ob die App Embedded Signup verwendet.",
			NOTHING_FOUND:
				"In dieser Autorisierung wurde kein WhatsApp-Business-Konto mit Nummer gefunden.",
			NO_TOKEN: "Für diesen Kanal ist kein Meta-Token hinterlegt.",
			DO_NOT_CONTACT: "Dieser Kontakt hat darum gebeten, nicht angesprochen zu werden.",

			NO_SUCH_PLAN: "Es gibt keinen Tarif mit diesem Code.",
			PLAN_ARCHIVED: "Dieser Tarif wird nicht mehr verkauft.",
			TRIAL_ALREADY_USED:
				"Die Testphase gibt es nur einmal — wähle einen Tarif, um weiterzumachen.",
			CHECKOUT_UNAVAILABLE:
				"Das Zahlungs-Gateway hat keinen Checkout geöffnet. Nichts wurde geändert — versuchen Sie es gleich noch einmal.",
			GATEWAY_UNAVAILABLE:
				"Bezahlte Tarife werden per Karte abgerechnet, und Kartenzahlung ist in dieser Installation nicht eingeschaltet. Nichts wurde geändert.",
			NO_PLAN: "Wähle einen Tarif, bevor du weitermachst.",
			PAID_NEEDS_CHECKOUT:
				"Ein bezahlter Tarif startet nur über den Checkout des Kunden; von hier aus nur kostenlose Tarife. Nichts wurde geändert.",
			USER_LIMIT: "In diesem Tarif ist kein Platz mehr für ein weiteres aktives Mitglied.",
			TRIAL_RAIL:
				"Pakete gibt es für bezahlte Tarife, und eine Testphase ist noch keiner. Wähle einen Tarif, dann öffnen sich die Pakete.",
			// Troca de plano para o plano de TESTE (409, 27/09/2026).
			TRIAL_TARGET:
				"Der Test ist nur für den Einstieg: Er kann nicht das Ziel eines Tarifwechsels sein. Wählen Sie einen der bezahlten Tarife.",
			SUBSCRIPTION_CANCELLED: "Dieses Abonnement ist gekündigt.",
			NO_CARD:
				"Pakete werden über die hinterlegte Karte abgerechnet, und dieses Abonnement hat keine. Hinterlege eine Karte und versuche es erneut.",
			CHARGE_DECLINED:
				"Die Karte wurde abgelehnt. Es wurde nichts abgebucht und es kamen keine Credits an — prüfen Sie die Karte oder nutzen Sie eine andere.",
			CHARGE_REQUIRES_ACTION:
				"Ihre Bank hat um Bestätigung dieses Kaufs gebeten. Er bleibt offen, bis Sie ihn freigeben; es wurde nichts abgebucht und es kamen noch keine Credits an.",
			CHARGE_AUTHENTICATED:
				"Authentifizierung abgeschlossen — die Credits sind auf Ihrem Guthaben.",
			NOT_WHOLE_BLOCKS:
				"Diese Erweiterung wird in ganzen Blöcken verkauft. Bitte eine runde Zahl davon angeben.",
			NO_SUCH_PACK: "Es gibt kein Paket mit diesem Code.",
			PACK_ARCHIVED: "Dieses Paket wird nicht mehr verkauft.",
			PACK_NOT_PRICED: "Dieses Paket wird in deiner Währung nicht verkauft.",
			CANNOT_BUY_PACK: "Das Paket konnte nicht gekauft werden.",
			CANNOT_CHOOSE_PLAN: "Dieser Tarif konnte nicht gewählt werden.",
			NOT_FOR_SALE: "Das gibt es nicht im Selbstbedienungskauf — sprich mit uns.",
			BELOW_USAGE: "Das liegt unter dem, was dieser Workspace schon nutzt.",
			EXTRAS_CHANGE_FAILED: "Die Zusatzoptionen konnten nicht geändert werden.",
			NO_SUBSCRIPTION: "Dieser Workspace hat kein Abonnement.",
			INVALID_REFERENCE_MONTH: "Der Referenzmonat lautet JJJJ-MM.",
			NO_INVOICE: "Für diesen Monat gibt es keine Rechnung.",
		},
		errorKind: {
			unauthorized: "Deine Sitzung ist abgelaufen. Melde dich neu an.",
			forbidden: "Dafür fehlt dir die Berechtigung.",
			notFound: "Das ist nicht mehr hier.",
			validation: "Einige Felder müssen korrigiert werden.",
			conflict: "Das steht im Konflikt mit etwas, das schon existiert.",
			rateLimited: "Zu viele Anfragen. Warte einen Moment.",
			paymentRequired: "Diese Tür ist in diesem Tarif geschlossen.",
			server: "Auf unserer Seite ist etwas schiefgegangen.",
			network: "Der Server war nicht erreichbar.",
			timeout: "Der Server hat zu lange zum Antworten gebraucht.",
		},
	},
} as const;
